import React from "react";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { HERO_IMAGE } from "../data/media";
import { WHATSAPP_LINK } from "../data/constants";

export const Hero = () => {
  const { t } = useLang();

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden grain" data-testid="hero-section">
      {/* Background */}
      <motion.div
        className="absolute inset-0"
        initial={{ scale: 1.12 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
      >
        <img src={HERO_IMAGE} alt="Phantom Residence curved tower" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/55 to-black/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
      </motion.div>

      <div className="relative px-6 md:px-12 lg:px-20 w-full pt-28">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="flex items-center gap-4"
          >
            <span className="w-12 gold-line" />
            <span className="overline">{t.hero.overline}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="mt-6 font-serif font-light text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#FFFFF0]"
          >
            {t.hero.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 }}
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#FFFFF0]/75 font-light"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="mt-6 flex items-start gap-3"
          >
            <span className="mt-1 w-8 gold-line shrink-0" />
            <p className="text-sm sm:text-base text-[#D4AF37] font-light italic max-w-md">
              {t.hero.promise}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="mt-10 flex flex-col sm:flex-row flex-wrap gap-3"
          >
            <button
              onClick={() => go("about")}
              className="px-7 py-3.5 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.18em] hover:bg-[#B5952F] transition-colors"
              data-testid="hero-cta-explore"
            >
              {t.hero.cta1}
            </button>
            <button
              onClick={() => go("contact")}
              className="px-7 py-3.5 border border-[#D4AF37] text-[#D4AF37] text-xs uppercase tracking-[0.18em] hover:bg-[#D4AF37]/10 transition-colors"
              data-testid="hero-cta-appointment"
            >
              {t.hero.cta2}
            </button>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="px-7 py-3.5 border border-white/20 text-[#FFFFF0]/85 text-xs uppercase tracking-[0.18em] hover:border-white/50 transition-colors"
              data-testid="hero-cta-whatsapp"
            >
              {t.hero.cta3}
            </a>
          </motion.div>
        </div>
      </div>

      <motion.button
        onClick={() => go("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#FFFFF0]/50 hover:text-[#D4AF37] transition-colors"
        data-testid="hero-scroll-down"
        aria-label="Scroll down"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="block">
          <ArrowDown size={22} strokeWidth={1.2} />
        </motion.span>
      </motion.button>
    </section>
  );
};
